import { Link } from "@tanstack/react-router";
import { Clock, ExternalLink, Video, X } from "lucide-react";

import type { AgendaEvent } from "@/api/db/agenda";
import { ContactButton } from "@/components/people/contact-button";
import { OverlayShell } from "@/components/ui/overlay";
import { cn } from "@/lib/utils";
import { AgendaLabels } from "@/lib/labels/agenda";
import { djs } from "@/lib/djs";

import { AgendaView } from "./agenda-view";
import { AgendaEventActions } from "./agenda-event-actions";

const SECTION_LABEL = "text-xs font-medium uppercase tracking-wide text-muted-foreground";

export const AGENDA_EVENT_MODAL_ID = "agenda-event-modal";

function PulsoMatch({
	patient,
	onClose,
}: {
	patient: NonNullable<AgendaEvent["patient"]>;
	onClose: () => void;
}) {
	return (
		<div>
			<p className={SECTION_LABEL}>{AgendaLabels.inPulso}</p>

			<div className="mt-1.5 flex flex-wrap items-center gap-2">
				{patient.person_id !== null && (
					<Link
						to="/pessoa/$id"
						params={{ id: patient.person_id }}
						onClick={onClose}
						className="inline-flex items-center gap-1 rounded-md border border-info/25 bg-info/10 px-2 py-0.5 text-xs font-medium leading-none text-info transition-colors hover:bg-info/20 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
					>
						{AgendaLabels.cadastroLink}
						<ExternalLink className="size-3" />
					</Link>
				)}

				{patient.person_id === null && (
					<p className="text-xs text-muted-foreground">{AgendaLabels.noMatch}</p>
				)}
			</div>
		</div>
	);
}

export function AgendaEventModal({ event, onClose }: { event: AgendaEvent; onClose: () => void }) {
	const date = event.start_at.slice(0, 10);

	return (
		<OverlayShell
			label={AgendaView.eventTitle(event)}
			onClose={onClose}
			className="items-center justify-center p-4"
		>
			<div
				id={AGENDA_EVENT_MODAL_ID}
				data-component="agenda-event-modal"
				className="overlay-panel max-w-md gap-4 p-5"
			>
				<header className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<div className={cn("flex flex-wrap items-center gap-x-3 gap-y-1", SECTION_LABEL)}>
							<span className="inline-flex items-center gap-1.5">
								<span className={cn("size-2 rounded-full", AgendaLabels.tipoDot[event.tipo])} />
								{AgendaLabels.tipo[event.tipo]}
							</span>
							<time dateTime={date}>
								{AgendaLabels.weekday(date)} {djs(date, "YYYY-MM-DD").format("DD/MM/YYYY")}
							</time>
						</div>

						<div className="mt-0.5 flex items-center gap-1.5">
							<h2 className="min-w-0 truncate type-section-title text-foreground">
								{AgendaView.eventTitle(event)}
							</h2>
							{event.patient?.person_id != null && (
								<ContactButton personId={event.patient.person_id} />
							)}
						</div>
					</div>

					<button
						type="button"
						onClick={onClose}
						aria-label="Fechar"
						className="-mr-1.5 inline-flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-border/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:size-8"
					>
						<X className="size-4" />
					</button>
				</header>

				<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
					<div className="flex flex-wrap items-center gap-2">
						<span className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-0.5 font-mono text-xs text-foreground">
							<Clock className="size-3 text-muted-foreground" />
							<time>{event.start_time}</time>
							<span className="font-sans text-muted-foreground">às</span>
							<time>{event.end_time}</time>
						</span>

						<span
							className={cn(
								"inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium leading-none",
								AgendaLabels.statusChip[event.status],
							)}
						>
							{AgendaLabels.status[event.status]}
						</span>

						{event.telemedicina && (
							<span className="inline-flex items-center gap-1 rounded-md border border-info/40 bg-info/10 px-2 py-0.5 text-xs font-medium leading-none text-info">
								<Video className="size-3" />
								{AgendaLabels.telemedicina}
							</span>
						)}
					</div>

					{event.patient !== null && <PulsoMatch patient={event.patient} onClose={onClose} />}

					{event.obs !== null && (
						<div>
							<p className={SECTION_LABEL}>Observações</p>
							<p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
								{event.obs}
							</p>
						</div>
					)}
				</div>

				<AgendaEventActions event={event} onDone={onClose} />
			</div>
		</OverlayShell>
	);
}
